import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { getPaymentsSalaryAligned } from "common/services/payments.service";
import useQueryState from "Components/Hooks/UseQueryState";
import moment from "moment";
import { FilterType, defaultFilter } from "../Filters";

type AlignedSummary = {
  yes: { totalAmount: number; totalCount: number };
  no: { totalAmount: number; totalCount: number };
  unspecified: { totalAmount: number; totalCount: number };
};
const extractData = (data: AlignedSummary) => {
  const propertyNames = Object.keys(data);

  const totalCounts = propertyNames.map((key) =>
    Number(data[key as keyof AlignedSummary].totalCount)
  );
  const totalAmount = propertyNames.map((key) =>
    Number(data[key as keyof AlignedSummary].totalAmount)
  );
  return { propertyNames, totalCounts, totalAmount };
};

const AmountDistributionChart = ({
  labels,
  series,
}: {
  labels: string[];
  series: number[];
}) => {
  const chartDonutBasicColors = ["#5BBDAB", "#F5784A", "#CCCCCC"];

  var options: any = {
    chart: {
      height: 300,
      type: "donut",
    },
    labels,
    legend: {
      position: "bottom",
    },
    dataLabels: {
      dropShadow: {
        enabled: false,
      },
    },
    colors: chartDonutBasicColors,
    tooltip: {
      enabled: true, // Enable tooltips
      y: {
        formatter: function (value: number) {
          return "$ " + value.toLocaleString(); // Format the tooltip value
        },
        title: {
          formatter: function (seriesName: string) {
            return `${seriesName}:`; // Customize the title of the tooltip
          },
        },
      },
    },
  };
  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={series}
      options={options}
      type="donut"
      height={300}
    />
  );
};

const ContractDistributionChart = ({
  labels,
  series,
}: {
  labels: string[];
  series: number[];
}) => {
  const chartDonutBasicColors = ["#5BBDAB", "#F5784A", "#CCCCCC"];

  var options: any = {
    chart: {
      height: 300,
      type: "donut",
    },
    labels,
    legend: {
      position: "bottom",
    },
    dataLabels: {
      dropShadow: {
        enabled: false,
      },
    },
    colors: chartDonutBasicColors,
    tooltip: {
      enabled: true, // Enable tooltips
      y: {
        formatter: function (value: number) {
          return value.toLocaleString() + " Contracts"; // Format the tooltip value
        },
        title: {
          formatter: function (seriesName: string) {
            return `${seriesName}:`; // Customize the title of the tooltip
          },
        },
      },
    },
  };
  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={series}
      options={options}
      type="donut"
      height={300}
    />
  );
};

const SalaryAlignedDistribution = () => {
  const [filter] = useQueryState<FilterType>("filter", defaultFilter);

  const [payments, setPayments] = useState<AlignedSummary>(
    {} as AlignedSummary
  );
  const [, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getPaymentsSalaryAligned({
          mdaId: filter?.mda?.value || undefined,
          positionTypeId: filter?.positionType?.value || undefined,
          agencyId: filter?.agency?.value || undefined,
          regionId: filter?.region?.value || undefined,
          paymentCategoryId: filter?.paymentCategory?.value || undefined,
          thematicAreaId: filter?.thematicArea?.value || undefined,
          sectorId: filter?.sector?.value || undefined,
          startDate: filter?.dateRange?.fromDate
            ? moment(filter.dateRange.fromDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
          endDate: filter?.dateRange?.toDate
            ? moment(filter.dateRange.toDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
        });

        setPayments(response);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    filter?.agency?.value,
    filter?.dateRange?.fromDate,
    filter?.dateRange?.toDate,
    filter.mda,
    filter?.positionType?.value,
    filter?.region?.value,
    filter?.sector?.value,
    filter?.thematicArea?.value,
    filter?.paymentCategory?.value,
  ]);

  const [activeTab, setActiveTab] = useState("number");

  const { totalCounts, propertyNames, totalAmount } = useMemo(
    () => extractData(payments),
    [payments]
  );

  return (
    <Card className="card-height-100">
      <CardHeader className="d-flex align-items-center ">
        <div className="flex-grow-1">
          <h4 className="card-title mb-0">
            Aligned with harmonized Salary Scale
          </h4>
        </div>
        <div className="d-flex gap-1">
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "number" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("number")}
          >
            NUMBER
          </button>
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "amount" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("amount")}
          >
            AMOUNT
          </button>
        </div>
      </CardHeader>
      <CardBody>
        {activeTab === "number" ? (
          <ContractDistributionChart
            labels={propertyNames}
            series={totalCounts}
          />
        ) : (
          <AmountDistributionChart
            labels={propertyNames}
            series={totalAmount}
          />
        )}
      </CardBody>
    </Card>
  );
};

export default SalaryAlignedDistribution;
